// assets
import {
    IconUserExclamation,
    IconReport,
    IconUsers,
    IconHeartRateMonitor,
    IconListCheck,
    IconSettings,
    IconCheckupList,
    IconServerBolt,
    IconFriends,
    IconTargetArrow,
    IconTableImport,
    IconTableAlias,
    IconChartDots3
} from '@tabler/icons';

function getPatientMenuConfig(clinicId, openTaskCount, vitalsAlertCount, isDirectReportsManagerOrHigher) {
    const taskItem = [
        {
            id: 'tasks-view',
            title: <span id="tasks-view">Tasks</span>,
            type: 'item',
            url: `/clinic/${clinicId}/tasks?initialAssignedToFilter=me`,
            icon: IconListCheck,
            breadcrumbs: false
        }
    ];
    if (openTaskCount > 0) {
        taskItem[0].chip = {
            label: openTaskCount,
            size: 'small',
            color: 'error',
            tooltip: 'You have tasks due today or tasks past due.'
        };
    }

    const vitalsAlertItem = [
        {
            id: 'vital-alerts',
            title: <span id="vital-alerts"> Vital Alerts</span>,
            type: 'item',
            url: `/clinic/${clinicId}/vitals/alerts`,
            icon: IconHeartRateMonitor,
            breadcrumbs: false
        }
    ];

    if (vitalsAlertCount > 0) {
        vitalsAlertItem[0].chip = {
            label: vitalsAlertCount,
            size: 'small',
            color: 'error',
            tooltip: 'There are open vitals alerts for this clinic.'
        };
    }

    // Team Performance (SYN-386): the Manager permission is what shows it; a
    // manager with no reports sees the page's empty state.
    const teamPerformanceItem = isDirectReportsManagerOrHigher
        ? [
              {
                  id: 'team-performance-view',
                  title: <span id="team-performance-title"> Team Performance </span>,
                  type: 'item',
                  url: '/team-performance',
                  icon: IconChartDots3,
                  breadcrumbs: false
              }
          ]
        : [];

    return {
        id: 'patient',
        title: <span id="patient"> Patients</span>,
        type: 'group',
        children: [
            {
                id: 'patients-list',
                title: <span id="patients">Patients</span>,
                type: 'item',
                url: `/clinic/${clinicId}/patients`,
                icon: IconUsers,
                breadcrumbs: false
            },
            ...vitalsAlertItem,
            ...taskItem,
            ...teamPerformanceItem
        ]
    };
}

function getClinicMenuConfig(clinicId, isClinicManagerOrHigher) {
    const clinicMenu = [];

    if (isClinicManagerOrHigher) {
        clinicMenu.push({
            id: 'reports-view',
            title: <span id="reports"> Reports </span>,
            type: 'item',
            url: `/clinic/${clinicId}/reports`,
            icon: IconReport,
            breadcrumbs: false
        });

        clinicMenu.push({
            id: 'clinic-details',
            title: <span id="clinic-details-title"> Clinic Details </span>,
            type: 'item',
            url: `/clinic/${clinicId}/details`,
            icon: IconSettings,
            breadcrumbs: false
        });

        clinicMenu.push({
            id: 'programmatic-tasks',
            title: <span id="programmatic-tasks-title"> Programmatic Tasks </span>,
            type: 'item',
            url: `/clinic/${clinicId}/programmatic-tasks`,
            icon: IconServerBolt,
            breadcrumbs: false
        });
    }

    return {
        id: 'clinic-settings',
        title: <span id="clinicsettings"> Clinic Settings</span>,
        type: 'group',
        children: clinicMenu
    };
}

function getOrgMenuConfig(isOrgAdmin, isTeamsManagerOrHigher, isProductivityManagerOrHigher, clinicId) {
    const orgMenu = [];

    if (isOrgAdmin) {
        orgMenu.push({
            id: 'protocols-creator-view',
            title: <span id="protocols-creator"> Protocol Templates </span>,
            type: 'item',
            url: `/protocols/templates`,
            icon: IconCheckupList,
            breadcrumbs: false
        });
        orgMenu.push({
            id: 'user-management-v2',
            title: <span id="user"> Users </span>,
            type: 'item',
            url: '/users',
            icon: IconUserExclamation,
            breadcrumbs: false
        });
        orgMenu.push({
            id: 'import-report-submission-management',
            title: <span id="import-report-submission-management"> Import Submissions </span>,
            type: 'item',
            url: '/import-report-submission-management',
            icon: IconTableAlias,
            breadcrumbs: false
        });
    }
    if (isTeamsManagerOrHigher) {
        orgMenu.push({
            id: 'organization-teams',
            title: <span id="organization-teams-title"> Teams Management </span>,
            type: 'item',
            url: '/organization-teams-management',
            icon: IconFriends,
            breadcrumbs: false
        });
    }
    if (isProductivityManagerOrHigher) {
        orgMenu.push({
            id: 'productivity-goals-view',
            title: <span id="productivity-goals-title"> Productivity Goals </span>,
            type: 'item',
            url: '/productivity-goals',
            icon: IconTargetArrow,
            breadcrumbs: false
        });
    }
    if (isOrgAdmin) {
        orgMenu.push({
            id: 'bulk-importer',
            title: <span id="bulk-importer"> Bulk Import </span>,
            type: 'item',
            url: `/clinic/${clinicId}/importer-tool`,
            icon: IconTableImport,
            breadcrumbs: false
        });
        orgMenu.push({
            id: 'org-settings-view',
            title: <span id="org-settings-view"> Organization Details </span>,
            type: 'item',
            url: '/org-settings',
            icon: IconSettings,
            breadcrumbs: false
        });
    }
    return {
        id: 'org-settings',
        title: <span id="orgsettings"> Org Settings</span>,
        type: 'group',
        children: orgMenu
    };
}

export function getMenuItems(
    clinicId,
    openTaskCount,
    isOrgAdmin,
    isClinicManagerOrHigher,
    isTeamsManagerOrHigher,
    isProductivityManagerOrHigher,
    vitalsAlertCount,
    isDirectReportsManagerOrHigher
) {
    const patientItems = getPatientMenuConfig(clinicId, openTaskCount, vitalsAlertCount, isDirectReportsManagerOrHigher);
    const clinicItems = getClinicMenuConfig(clinicId, isClinicManagerOrHigher);
    const orgItems = getOrgMenuConfig(isOrgAdmin, isTeamsManagerOrHigher, isProductivityManagerOrHigher, clinicId);

    const menuItems = [];
    if (patientItems?.children?.length > 0) {
        menuItems.push(patientItems);
    }
    if (clinicItems?.children?.length > 0) {
        menuItems.push(clinicItems);
    }
    if (orgItems?.children?.length > 0) {
        menuItems.push(orgItems);
    }

    return menuItems;
}
